import type { AuthOrganizationDto } from '@osteopro/shared';
import { useQueryClient } from '@tanstack/react-query';
import { use, useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import { AuthContext, type AuthContextValue } from './auth-context.js';
import { isPlatformOnly } from './platform-role.js';
import { clearSuspension } from './suspension-store.js';
import {
  bootstrapAuth,
  endImpersonation,
  getAuthSnapshot,
  login,
  logout,
  logoutEverywhere,
  register,
  reloadIdentity,
  setCacheResetter,
  startImpersonation,
  subscribeAuth,
  switchOrganization,
} from './auth-store.js';

/** Liaison React du magasin d'authentification. Le magasin reste la source ; ceci n'est qu'une vue. */

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const snapshot = useSyncExternalStore(subscribeAuth, getAuthSnapshot, getAuthSnapshot);

  useEffect(() => {
    setCacheResetter(() => {
      queryClient.clear();
      // Changement de cabinet ou déconnexion : la suspension du cabinet quitté ne vaut plus.
      // Un praticien membre de deux cabinets, dont un seul est suspendu, travaille dans l'autre.
      clearSuspension();
    });
  }, [queryClient]);

  useEffect(() => {
    void bootstrapAuth();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...snapshot,
      isAuthenticated: snapshot.status === 'authenticated',
      isLoading: snapshot.status === 'loading',
      login,
      register,
      logout,
      logoutEverywhere,
      switchOrganization,
      reloadIdentity,
      startImpersonation,
      endImpersonation,
    }),
    [snapshot],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);
  if (!context) throw new Error('useAuth doit être utilisé dans un AuthProvider.');
  return context;
}

/** Vrai pour un compte de plateforme sans cabinet : voir `isPlatformOnly`. */
export function usePlatformOnly(): boolean {
  const { user, organizations, impersonation } = useAuth();
  return isPlatformOnly({
    platformRole: user?.platformRole,
    organizationCount: organizations.length,
    impersonating: impersonation !== null,
  });
}

export interface OrganizationContextValue {
  organization: AuthOrganizationDto | null;
  organizations: AuthOrganizationDto[];
  /** Rôle de l'utilisateur courant dans le cabinet actif, tel que le serveur le renvoie. */
  isOwner: boolean;
  canSwitch: boolean;
  switchOrganization: (organizationId: string) => Promise<void>;
}

/**
 * Le cabinet actif, dérivé d'une seule source (`/api/auth/me`). Aucun écran ne va rechercher
 * l'organisation par lui-même, et surtout aucun ne prend « la première de la liste ».
 */
export function useOrganization(): OrganizationContextValue {
  const { organizations, activeOrganizationId, switchOrganization: change } = useAuth();
  return useMemo(() => {
    const organization = organizations.find((item) => item.id === activeOrganizationId) ?? null;
    return {
      organization,
      organizations,
      isOwner: organization?.role === 'owner',
      canSwitch: organizations.length > 1,
      switchOrganization: change,
    };
  }, [organizations, activeOrganizationId, change]);
}
